import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ReportFilterLocation from './ReportFilterLocation';
import useFetch from 'use-http';
import { HorizonInterval } from 'src/models/Horizon';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import Tag from 'src/models/Tag';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import ReportHorizonSequencing from 'src/models/ReportHorizonSequencing';
import ReportName from 'src/models/ReportName';
import FilteredReport from 'src/models/FilteredReport';
import ReportTownshipDto from 'src/models/reports/ReportTownshipDto';
import ReportHorizonDto from 'src/models/reports/ReportHorizonDto';

interface IReportFilterProps {
    reportNumber: number;
    reportName: ReportName;
    horizonIntervals: HorizonInterval[];
    // selectedHorizons: HorizonInterval[];
    selectedTags: Tag[];
    selectedSequence: ReportHorizonSequencing;
    updateHorizonIntervals: (horizons: ReportHorizonDto[]) => void;
    onGetReportId: (reportId: number) => void;
}

const ReportFilter = (props: IReportFilterProps) => {
    const appContext = useContext<IAppContextState>(AppContext);

    const { project } = useSelector((state: IRootState) => state);
    const { getValues, setValue, register } = useForm();
    const { get, post, response } = useFetch();

    const [tagFilteredSelections, setTagFilteredSelections] = useState<string[]>([]);
    const [townships, setTownships] = useState<ReportTownshipDto[]>([]);
    const [selectedHorizons, setSelectedHorizons] = useState<ReportHorizonDto[]>([]);
    // Set to prevent triple-nested for-loop logic.
    const [flatHorizonList, setFlatHorizonList] = useState<ReportHorizonDto[]>([]);
    const [loadingFields, setLoadingFields] = useState<boolean>(true);
    
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [expandAll, setExpandAll] = useState<boolean>(false);
    // const watchAllProject = watch('allProject', 'true');

    useEffect(() => {
        if (!!project.currentProject) {
            getTownships();
        }
    }, [project]);

    useEffect(() => {
        if (props.selectedTags?.length > 0) {
            getTagFilteredItems();
        } else {
            updateFilteredTagSelections([], true);
        }
    }, [props.selectedTags]);

    useEffect(() => {
        updateFilteredTagSelections(tagFilteredSelections, false);
        if (townships?.length > 0) {
            let hList: ReportHorizonDto[] = [];

            // TODO: There's almost certainly a some Javascript function that can handle this.
            // Flatten is what I was seeing, but it looks like our version doesn't have that.
            for (let t of townships) {
                for (let fl of t.fieldList) {
                    for (let tr of fl.tractList) {
                        hList = hList.concat(tr.horizonList);
                    }
                }
            }

            setFlatHorizonList(hList);
        }
    }, [townships]);

    useEffect(() => {
        processHorizonIntervals();
    }, [selectedHorizons]);

    const getTownships = async () => {
        await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/report-townships/${project.currentProject.landSystemId}`).then((result) => {
            setTownships(result);
        }).catch((error) => {
            console.log('Error getting Townships', error);
        });
    };

    const getTagFilteredItems = async () => {
        let tagString = props.selectedTags.map((st) => st.name).join(',');

        let newTagGlobals = await get(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/tag-list/${tagString}`);

        if (response.ok) {
            updateFilteredTagSelections(newTagGlobals, true);
        } else {
            window.alert('Unable to retrieve Global Ids from Tags.');
        }
    };

    const runReport = async () => {
        // Make sure this will still work when sequencing is not required.
        let idList = getGlobalFromCheckList();
        let sequenceBody: FilteredReport = {
            filters: idList.join(','),
            horizonSequences: null
        };
        
        if (!!props.selectedSequence) {
            sequenceBody.horizonSequences = props.selectedSequence.horizonSequences;
        } else if (props.reportName.sequencingRequired === true) {
            window.alert('Horizon Sequencing required!');
            return;
        }
        
        if (idList.length > 0) {
            let newReportId = await post(`v1.0/reports/${props.reportName.id}/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/by-filter`, sequenceBody);
            if (response.ok) {  
                props.onGetReportId(newReportId);
            } else {
                window.alert('Report Generation Failed. Please Contact System Admin');
            }
        }
    };

    const getGlobalFromCheckList = () => {
        let currentForm = getValues();
        let formKeys = Object.entries(currentForm)
        .filter((p: [string, unknown]) => p[0] !== 'but_select_all' && p[0] !== 'but_expand_all');

        let globals: string[] = [];
        let tagFilters = [...tagFilteredSelections];
        tagFilters = tagFilters.map((tg) => tg.split(',').join('.'));
        
        for (const [key, value] of formKeys) {
            let keyGlobal = key.split(',').join('.');

            if (value === true && !tagFilters.includes(keyGlobal)) {
                globals.push(keyGlobal);
            } else if (value === false && tagFilters.includes(keyGlobal)) {
                tagFilters.splice(tagFilters.indexOf(key), 1);
            }
        }

        globals = globals.concat(tagFilters);

        return globals;
    };

    // Need to grab the Township based on the Section, as Township is not connected to a global id.
    const updateFilteredTagSelections = (newGlobalIds: string[], refreshTagList: boolean) => {
        let currentForm = getValues();
        let currentKeys = Object.keys(currentForm);

        let globalKeys = newGlobalIds.map(ng => ng.split('.').join(','));
        let currentCompare = currentKeys.map(ck => ck.split('.').join(','));
        let childKeys: string[] = [];
        let removeKeys: string[] = [];
        
        for (let global of globalKeys) {
            setValue(global, true);
            let children = currentCompare.filter(ck => ck.substring(0, global.length) === global);
            childKeys = childKeys.concat(children);
        }
        for (let ck of childKeys) {
            setValue(ck, true);
        }

        // TODO: Work on consolidating all of this logic so we don't have to do these transforms.
        let hCheck = flatHorizonList.filter(hl => childKeys.includes(hl.globalId.replace('-', ',').split('.').join(',')));
        let updateSelect = [...selectedHorizons];
        for (let newSelect of hCheck) {
            if (!selectedHorizons.includes(newSelect)) {
                updateSelect = updateSelect.concat(newSelect);
            }
        }

        if (refreshTagList === true) {
            let prevTags = [...tagFilteredSelections];
    
            for (const key of currentKeys) {
                if (prevTags.includes(key) && !(globalKeys.includes(key) || childKeys.includes(key))) {
                    let children = currentCompare.filter(ck => ck.substring(0, key.length) === key);
                    removeKeys = removeKeys.concat(children);
                    setValue(key, false);
                }
            }

            for (let rk of removeKeys) {
                setValue(rk, false);
            }

            let rCheck = selectedHorizons.filter(sl => removeKeys.includes(sl.globalId.replace('-', ',').split('.').join(',')));
            updateSelect = updateSelect.filter(us => !rCheck.includes(us));
    
            setTagFilteredSelections(globalKeys);
        }

        setSelectedHorizons(updateSelect);
    };

    const updateSelectAll = () => {
        let currentForm = getValues();
        setSelectAll(currentForm.but_select_all === true);
    };

    const updateExpandAll = () => {
        let currentForm = getValues();
        setExpandAll(currentForm.but_expand_all === true);
    };

    const processHorizonIntervals = () => {
        let hSummaries = flatHorizonList.filter(h => selectedHorizons.includes(h));
        props.updateHorizonIntervals(hSummaries);
    };

    return (
        <div>
            {/* <RHFLegend title="Filter" /> */}
            <div style={{ fontSize: '1.75rem' }}>Filter</div>

            <div>
                <form>
                    <div>
                        <div style={{ height: '47vh', overflow: 'auto' }}>
                            <div>
                                {/* NOTE: ALL INPUT BUTTONS CONNECTED TO FORM MUST START WITH 'BUT' */}
                                <div style={{ display: 'flex' }}>
                                    <input 
                                        type="checkbox" 
                                        id="but_select_all" 
                                        name="but_select_all"
                                        ref={register}
                                        onChange={() => updateSelectAll()}
                                        disabled={loadingFields}
                                    />
                                    <label htmlFor="selectAll">Select All</label>

                                    <div style={{ margin: '0 1rem' }}>|</div>

                                    <input 
                                        type="checkbox" 
                                        id="but_expand_all" 
                                        name="but_expand_all"
                                        ref={register}
                                        onChange={() => updateExpandAll()}
                                        disabled={loadingFields}
                                    />
                                    <label htmlFor="selectAll">Expand All</label>
                                </div>
                            </div>
                            <div>
                                <div>
                                    <ReportFilterLocation
                                        landSystemId={project.currentProject?.landSystemId} 
                                        townships={townships}
                                        // selectedHorizonIds={props.selectedHorizons.map(sh => sh.horizonId)}
                                        selectAll={selectAll}
                                        expandAll={expandAll}
                                        loadingFields={loadingFields}
                                        selectedHorizons={selectedHorizons}
                                        refreshTagValues={() => updateFilteredTagSelections(tagFilteredSelections, false)}
                                        getValues={getValues}
                                        register={register}
                                        setValue={setValue}
                                        setLoadingFields={(loading: boolean) => setLoadingFields(loading)}
                                        setSelectedHorizons={(horizons: ReportHorizonDto[]) => setSelectedHorizons(horizons)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div style={{ display: 'flex flex-end' }}>
                                <a style={{ display: 'hidden' }} id="rep" />
                                <div>
                                    <Button text="Run Report" intent="primary" onClick={runReport} />
                                </div>
                                <div>
                                    <div style={{ display: 'flex' }}>
                                        <input type="radio" id="excel" name="mor_output_type" defaultChecked={true} />
                                        <label htmlFor="excel">Excel</label>

                                        <div style={{ margin: '0 1rem' }}>|</div>

                                        <input type="radio" id="pdf" name="mor_output_type" />
                                        <label htmlFor="pdf">PDF</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReportFilter;



// WEBPACK FOOTER //
// ./src/components/project/sections/ReportComponents/ReportFilter.tsx